import { useEffect, useState } from 'react';
import { memberApi, type Member } from '../../api';
import Select from '../../components/common/Select';
import { useAuth } from '../../contexts/AuthContext';

// Self-profile editor. Advocate-specific fields (title, seniority, FFC
// number, advocate class, PMT, CPD, bar membership, VAT default) live on
// tenant_members — editable by each user without members.manage capability.

export default function SettingsProfile() {
  const { user } = useAuth();
  const [member, setMember] = useState<Member | null>(null);
  const [form, setForm] = useState<Partial<Member>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok'|'err'; text: string } | null>(null);

  useEffect(() => {
    memberApi.getSelf()
      .then((r) => { setMember(r.member); setForm({}); })
      .catch((e) => setMessage({ kind: 'err', text: (e as Error).message }));
  }, []);

  const v = <K extends keyof Member>(k: K): Member[K] | undefined =>
    (form[k] !== undefined ? form[k] : (member?.[k])) as Member[K] | undefined;
  const set = <K extends keyof Member>(k: K, val: Member[K]) => setForm({ ...form, [k]: val });

  const save = async () => {
    if (!Object.keys(form).length) return;
    setSaving(true); setMessage(null);
    try {
      await memberApi.updateSelf(form);
      const r = await memberApi.getSelf();
      setMember(r.member); setForm({});
      setMessage({ kind: 'ok', text: 'Saved.' });
    } catch (e) { setMessage({ kind: 'err', text: (e as Error).message }); }
    finally { setSaving(false); }
  };

  if (!member) return <div className="muted">Loading…</div>;

  return (
    <div className="stack" style={{ maxWidth: 640 }}>
      <div>
        <h1>My profile</h1>
        <p className="muted">Your advocate-specific details as stored for this chambers. Role and billing-contact changes go through Members.</p>
      </div>

      <div className="card stack">
        <div className="row">
          <div style={{ width: 120 }}>
            <label>Title</label>
            <input value={(v('title') as string) || ''} onChange={(e) => set('title', e.target.value)} placeholder="Adv / Mr / Ms" />
          </div>
          <div className="grow">
            <label>Display name</label>
            <input value={(v('display_name') as string) || ''} onChange={(e) => set('display_name', e.target.value)} />
          </div>
        </div>

        <div>
          <label>Email</label>
          <input value={user?.email || ''} disabled />
        </div>

        <div className="row">
          <div className="grow">
            <label>Advocate class</label>
            <Select
              value={(v('advocate_class') as string) || ''}
              onChange={(val) => set('advocate_class', (val as Member['advocate_class']) || undefined)}
              options={[
                { value: '',              label: '\u2014 unset \u2014' },
                { value: 'referral',      label: 'Referral advocate (Briefs from attorneys only)' },
                { value: 'trust_account', label: 'Trust-account advocate (direct briefs, trust)' },
              ]}
            />
          </div>
          <div className="grow">
            <label>Seniority</label>
            <Select
              value={(v('seniority') as string) || ''}
              onChange={(val) => set('seniority', val)}
              options={[
                { value: '',       label: '\u2014 unset \u2014' },
                { value: 'junior', label: 'Junior' },
                { value: 'senior', label: 'Senior' },
                { value: 'sc',     label: 'Senior Counsel (SC)' },
              ]}
            />
          </div>
        </div>

        <div className="row">
          <div className="grow">
            <label>FFC number</label>
            <input value={(v('ffc_number') as string) || ''} onChange={(e) => set('ffc_number', e.target.value)} />
          </div>
          <div className="grow">
            <label>FFC expires</label>
            <input type="date" value={(v('ffc_expires_at') as string) || ''} onChange={(e) => set('ffc_expires_at', e.target.value)} />
          </div>
        </div>

        <div className="row">
          <div className="grow">
            <label>Bar membership</label>
            <input value={(v('bar_membership') as string) || ''} onChange={(e) => set('bar_membership', e.target.value)} placeholder="e.g. Johannesburg Bar" />
          </div>
          <div className="grow">
            <label>PMT completed</label>
            <input type="date" value={(v('pmt_completed_at') as string) || ''} onChange={(e) => set('pmt_completed_at', e.target.value)} />
          </div>
        </div>

        <div className="row">
          <div className="grow">
            <label>CPD points (current year)</label>
            <input type="number" step="0.5" value={(v('cpd_points_current') as number | undefined) ?? ''} onChange={(e) => set('cpd_points_current', e.target.value === '' ? undefined : (Number(e.target.value) as Member['cpd_points_current']))} />
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={!!(v('default_vat_registered' as keyof Member))}
            onChange={(e) => set('default_vat_registered' as keyof Member, e.target.checked as never)}
            style={{ width: 'auto' }}
          />
          <span>I am registered for VAT by default (affects new invoice lines)</span>
        </label>

        {message && (
          <div style={{ color: message.kind === 'ok' ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {message.text}
          </div>
        )}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="primary" onClick={save} disabled={saving || !Object.keys(form).length}>
            {saving ? 'Saving\u2026' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
